import {
  Atom,
  BarChart3,
  Bell,
  BookOpen,
  CirclePlus,
  ClipboardList,
  Compass,
  FlaskConical,
  FolderKanban,
  Gamepad2,
  Gift,
  GraduationCap,
  Laptop,
  Rocket,
  LayoutDashboard,
  LibraryBig,
  MessageCircle,
  MoreHorizontal,
  ReceiptText,
  RefreshCw,
  Scale,
  Settings,
  Trophy,
  UserRoundSearch,
  WalletCards,
  type LucideIcon,
} from 'lucide-react';
import { matchPath } from 'react-router-dom';
import { CHALLENGE_PORTAL_PATH } from '@/lib/challenge';

export interface PortalNavItem {
  id: string;
  to: string;
  label: string;
  mobileLabel?: string;
  description?: string;
  icon: LucideIcon;
  end?: boolean;
  external?: boolean;
}

export interface PortalNavSection {
  id: string;
  /** Section heading; null for the ungrouped top area (Dashboard). */
  label: string | null;
  items: PortalNavItem[];
}

export interface PortalWalletMenuItem {
  id: string;
  to: string;
  label: string;
  description: string;
  icon: LucideIcon;
}

export const PORTAL_NAV_SECTION_IDS = {
  OVERVIEW: 'overview',
  EXPLORE: 'explore',
  NOTIFICATIONS: 'notifications',
  MORE: 'more',
  FAMILY: 'family',
  ACCOUNT: 'account',
} as const;

export const PORTAL_NAV_ITEM_IDS = {
  DASHBOARD: 'dashboard',
  EXPLORE: 'explore-home',
  PHYSICS_LAB: 'physics-lab',
  CHEMISTRY_LAB: 'chemistry-lab',
  MORE: 'more-home',
  CLASSES: 'classes',
  SCHOOL_LEARNING: 'school-learning',
  STUDENT_WORK: 'student-work',
  TEACHERS: 'teachers',
  ACADEMY: 'academy',
  SELF_PACED: 'self-paced',
  CHALLENGE: 'challenge',
  GUIDES: 'guides',
  SCHOOL_RESEARCH: 'school-research',
  MY_PROJECTS: 'my-projects',
  PARENT_COURSES: 'parent-courses',
  HSC: 'hsc',
  SENIOR_PATHWAY: 'senior-pathway',
  APPROVALS: 'approvals',
  USAGE: 'usage',
  DESKTOP: 'desktop',
  REWARDS: 'rewards',
  MEMBERSHIP: 'membership',
  WALLET: 'wallet',
  BILLING: 'billing',
  SETTINGS: 'settings',
} as const;

type PortalNavItemId = (typeof PORTAL_NAV_ITEM_IDS)[keyof typeof PORTAL_NAV_ITEM_IDS];

export const PORTAL_DOCK_ITEM_IDS = {
  HOME: 'dock-home',
  EXPLORE: 'dock-explore',
  NOTIFICATIONS: 'dock-notifications',
  MORE: 'dock-more',
} as const;

export type PortalNavSectionId =
  (typeof PORTAL_NAV_SECTION_IDS)[keyof typeof PORTAL_NAV_SECTION_IDS];

interface PortalDockItemBase {
  id: string;
  label: string;
  icon: LucideIcon;
  trackedSectionId?: PortalNavSectionId;
}

export interface PortalDockLinkItem extends PortalDockItemBase {
  kind: 'link';
  to: string;
  end?: boolean;
}

export type PortalDockItem = PortalDockLinkItem;

// Grouped IA per parent-portal-prd.md §2: Explore (discover, learn and enrol),
// Notifications (time-sensitive decisions), Family (monitor and control),
// Account (money and settings).
export const PORTAL_NAV_SECTIONS: PortalNavSection[] = [
  {
    id: PORTAL_NAV_SECTION_IDS.OVERVIEW,
    label: null,
    items: [
      {
        id: PORTAL_NAV_ITEM_IDS.DASHBOARD,
        to: '/portal',
        label: 'Dashboard',
        mobileLabel: 'Home',
        icon: LayoutDashboard,
        end: true,
      },
    ],
  },
  {
    id: PORTAL_NAV_SECTION_IDS.EXPLORE,
    label: 'Explore',
    items: [
      {
        id: PORTAL_NAV_ITEM_IDS.EXPLORE,
        to: '/portal/explore',
        label: 'Explore',
        icon: Compass,
      },
      {
        id: PORTAL_NAV_ITEM_IDS.PHYSICS_LAB,
        to: '/labs/physics?from=portal',
        label: 'Physics Lab',
        icon: Atom,
      },
      {
        id: PORTAL_NAV_ITEM_IDS.CHEMISTRY_LAB,
        to: '/labs/chemistry?from=portal',
        label: 'Virtual Chemistry Lab',
        icon: FlaskConical,
      },
      {
        id: PORTAL_NAV_ITEM_IDS.CLASSES,
        to: '/portal/courses',
        label: 'Courses',
        icon: BookOpen,
      },
      {
        id: PORTAL_NAV_ITEM_IDS.SCHOOL_LEARNING,
        to: '/portal/school-learning',
        label: 'School learning',
        description: 'Verified school and tutor learning, reports and notices',
        icon: GraduationCap,
      },
      // Student Work Gallery (student-work-gallery-prd.md §5, J2).
      {
        id: PORTAL_NAV_ITEM_IDS.STUDENT_WORK,
        to: '/portal/gallery',
        label: 'Student Work',
        icon: Gamepad2,
      },
      {
        id: PORTAL_NAV_ITEM_IDS.TEACHERS,
        to: '/portal/tutoring',
        label: 'Tutoring',
        icon: UserRoundSearch,
      },
      {
        id: PORTAL_NAV_ITEM_IDS.SELF_PACED,
        to: '/portal/self-paced',
        label: 'Online Courses',
        mobileLabel: 'Online',
        icon: Rocket,
      },
      {
        id: PORTAL_NAV_ITEM_IDS.ACADEMY,
        to: '/portal/academy',
        label: 'Exam Prep',
        icon: GraduationCap,
      },
      {
        id: PORTAL_NAV_ITEM_IDS.GUIDES,
        to: '/portal/guides',
        label: 'Family Guides',
        mobileLabel: 'Guides',
        icon: LibraryBig,
      },
      {
        id: PORTAL_NAV_ITEM_IDS.SCHOOL_RESEARCH,
        to: '/portal/school-research',
        label: 'School compare',
        icon: Scale,
      },
      // The ONLY route into the challenge from inside the Portal. Without it the
      // register/report pages are reachable exclusively from the marketing
      // site's CTA, so a parent who signed the consent and left could not get
      // back to their own entry. The target page renders every state — not
      // registered, mid-payment, confirmed — so one link covers the lot.
      {
        id: PORTAL_NAV_ITEM_IDS.CHALLENGE,
        to: CHALLENGE_PORTAL_PATH,
        label: 'Creative Code Challenge',
        mobileLabel: 'Challenge',
        icon: Trophy,
      },
    ],
  },
  {
    id: PORTAL_NAV_SECTION_IDS.NOTIFICATIONS,
    label: 'Notifications',
    items: [
      {
        id: PORTAL_NAV_ITEM_IDS.APPROVALS,
        to: '/portal/approvals',
        label: 'Notifications',
        icon: Bell,
      },
    ],
  },
  {
    id: PORTAL_NAV_SECTION_IDS.MORE,
    label: 'More',
    items: [
      {
        id: PORTAL_NAV_ITEM_IDS.MORE,
        to: '/portal/more',
        label: 'More',
        icon: MoreHorizontal,
      },
    ],
  },
  {
    id: PORTAL_NAV_SECTION_IDS.FAMILY,
    label: 'Family',
    items: [
      {
        id: PORTAL_NAV_ITEM_IDS.PARENT_COURSES,
        to: '/portal/learning',
        label: 'Parent courses',
        icon: BookOpen,
      },
      // Parent-owned projects (private to the owner; kids never see them).
      {
        id: PORTAL_NAV_ITEM_IDS.MY_PROJECTS,
        to: '/portal/projects',
        label: 'My Projects',
        mobileLabel: 'Projects',
        icon: FolderKanban,
      },
      {
        id: PORTAL_NAV_ITEM_IDS.HSC,
        to: '/portal/academy/hsc-planner',
        label: 'HSC Planner',
        icon: ClipboardList,
      },
      {
        id: PORTAL_NAV_ITEM_IDS.SENIOR_PATHWAY,
        to: '/portal/pathways',
        label: 'My Study Plan',
        icon: GraduationCap,
      },
      { id: PORTAL_NAV_ITEM_IDS.USAGE, to: '/portal/usage', label: 'Usage', icon: BarChart3 },
      {
        id: PORTAL_NAV_ITEM_IDS.DESKTOP,
        to: '/portal/desktop',
        label: 'Hangar computers',
        mobileLabel: 'Hangar',
        icon: Laptop,
      },
    ],
  },
  {
    id: PORTAL_NAV_SECTION_IDS.ACCOUNT,
    label: 'Account',
    items: [
      {
        id: PORTAL_NAV_ITEM_IDS.REWARDS,
        to: '/portal/rewards',
        label: 'Rewards & credit',
        icon: Gift,
      },
      { id: PORTAL_NAV_ITEM_IDS.MEMBERSHIP, to: '/portal/membership', label: 'Membership & Stars', icon: GraduationCap },
      { id: PORTAL_NAV_ITEM_IDS.WALLET, to: '/portal/wallet', label: 'Wallet', icon: WalletCards },
      {
        id: PORTAL_NAV_ITEM_IDS.BILLING,
        to: '/portal/billing',
        label: 'Billing',
        icon: ReceiptText,
      },
      {
        id: PORTAL_NAV_ITEM_IDS.SETTINGS,
        to: '/portal/settings',
        label: 'Settings',
        icon: Settings,
      },
    ],
  },
];

export const PORTAL_NAV_ITEMS: PortalNavItem[] = PORTAL_NAV_SECTIONS.flatMap(
  (section) => section.items,
);

function getPortalNavItem(itemId: string): PortalNavItem {
  const item = PORTAL_NAV_ITEMS.find((candidate) => candidate.id === itemId);
  if (!item) {
    throw new Error(`Portal navigation item is missing: ${itemId}`);
  }
  return item;
}

/**
 * More contains secondary planning and account destinations, plus one duplicate
 * route into Stars usage for discoverability. The Stars control remains the
 * fastest route into usage and owns the rest of the wallet actions.
 */
const PORTAL_MORE_ROUTE_ITEMS: PortalNavItem[] = [
  PORTAL_NAV_ITEM_IDS.PHYSICS_LAB,
  PORTAL_NAV_ITEM_IDS.CHEMISTRY_LAB,
  PORTAL_NAV_ITEM_IDS.PARENT_COURSES,
  PORTAL_NAV_ITEM_IDS.MY_PROJECTS,
  PORTAL_NAV_ITEM_IDS.HSC,
  PORTAL_NAV_ITEM_IDS.SENIOR_PATHWAY,
  PORTAL_NAV_ITEM_IDS.USAGE,
  PORTAL_NAV_ITEM_IDS.BILLING,
  PORTAL_NAV_ITEM_IDS.SETTINGS,
].map(getPortalNavItem);

const getMoreItem = (itemId: string): PortalNavItem => {
  const item = PORTAL_MORE_ROUTE_ITEMS.find((candidate) => candidate.id === itemId);
  if (!item) throw new Error(`More navigation item is missing: ${itemId}`);
  return { ...item };
};

export const PORTAL_CONTACT_URL = 'https://airbotix.ai/contact';

const PORTAL_MORE_STARS_USAGE_ITEM: PortalNavItem = {
  ...getMoreItem(PORTAL_NAV_ITEM_IDS.USAGE),
  label: 'Stars & usage',
};

const PORTAL_MORE_BILLING_ITEM: PortalNavItem = {
  ...getMoreItem(PORTAL_NAV_ITEM_IDS.BILLING),
  label: 'Billing & payments',
};

const PORTAL_CONTACT_ITEM: PortalNavItem = {
  id: 'contact-support',
  to: PORTAL_CONTACT_URL,
  label: 'Contact support',
  icon: MessageCircle,
  external: true,
};

export const PORTAL_MORE_NAV_ITEMS: PortalNavItem[] = [
  getMoreItem(PORTAL_NAV_ITEM_IDS.PHYSICS_LAB),
  getMoreItem(PORTAL_NAV_ITEM_IDS.CHEMISTRY_LAB),
  getMoreItem(PORTAL_NAV_ITEM_IDS.PARENT_COURSES),
  getMoreItem(PORTAL_NAV_ITEM_IDS.MY_PROJECTS),
  getMoreItem(PORTAL_NAV_ITEM_IDS.HSC),
  getMoreItem(PORTAL_NAV_ITEM_IDS.SENIOR_PATHWAY),
  PORTAL_MORE_STARS_USAGE_ITEM,
  PORTAL_MORE_BILLING_ITEM,
  getMoreItem(PORTAL_NAV_ITEM_IDS.SETTINGS),
  PORTAL_CONTACT_ITEM,
];

export const PORTAL_MORE_NAV_SECTIONS: PortalNavSection[] = [
  {
    id: 'planning-tools',
    label: 'Create and plan',
    items: [
      getMoreItem(PORTAL_NAV_ITEM_IDS.PHYSICS_LAB),
      getMoreItem(PORTAL_NAV_ITEM_IDS.CHEMISTRY_LAB),
      getMoreItem(PORTAL_NAV_ITEM_IDS.PARENT_COURSES),
      getMoreItem(PORTAL_NAV_ITEM_IDS.MY_PROJECTS),
      getMoreItem(PORTAL_NAV_ITEM_IDS.HSC),
      getMoreItem(PORTAL_NAV_ITEM_IDS.SENIOR_PATHWAY),
    ],
  },
  {
    id: 'account-support',
    label: 'Account and support',
    items: [
      PORTAL_MORE_STARS_USAGE_ITEM,
      PORTAL_MORE_BILLING_ITEM,
      getMoreItem(PORTAL_NAV_ITEM_IDS.SETTINGS),
      PORTAL_CONTACT_ITEM,
    ],
  },
];

/**
 * The header Stars control owns Stars balance, consumption and refill actions.
 * Billing is account administration and lives in More instead.
 */
export const PORTAL_WALLET_MENU_ITEMS: PortalWalletMenuItem[] = [
  {
    id: 'wallet-top-up',
    to: '/portal/wallet/topup',
    label: 'Top up Stars',
    description: 'Choose a Stars pack.',
    icon: CirclePlus,
  },
  {
    id: PORTAL_NAV_ITEM_IDS.USAGE,
    to: '/portal/usage',
    label: 'Star usage',
    description: 'See usage by kid and task.',
    icon: BarChart3,
  },
  {
    id: PORTAL_NAV_ITEM_IDS.WALLET,
    to: '/portal/wallet',
    label: 'Wallet and limits',
    description: 'View activity and set spending limits.',
    icon: WalletCards,
  },
  {
    id: 'wallet-auto-top-up',
    to: '/portal/wallet/auto-topup',
    label: 'Auto top-up',
    description: 'Set a low-balance refill.',
    icon: RefreshCw,
  },
];

const PORTAL_WALLET_ROUTE_PATTERNS = [
  '/portal/wallet',
  '/portal/wallet/*',
  '/portal/usage',
  '/portal/usage/*',
] as const;

export function isPortalWalletPath(pathname: string): boolean {
  return PORTAL_WALLET_ROUTE_PATTERNS.some((path) => matchPath({ path, end: true }, pathname));
}

const HOME_DOCK_ITEM: PortalDockLinkItem = {
  kind: 'link',
  id: PORTAL_DOCK_ITEM_IDS.HOME,
  to: '/portal',
  label: 'Home',
  icon: LayoutDashboard,
  end: true,
  trackedSectionId: PORTAL_NAV_SECTION_IDS.OVERVIEW,
};

const EXPLORE_DOCK_ITEM: PortalDockLinkItem = {
  kind: 'link',
  id: PORTAL_DOCK_ITEM_IDS.EXPLORE,
  to: '/portal/explore',
  label: 'Explore',
  icon: Compass,
  trackedSectionId: PORTAL_NAV_SECTION_IDS.EXPLORE,
};

const NOTIFICATIONS_DOCK_ITEM: PortalDockLinkItem = {
  kind: 'link',
  id: PORTAL_DOCK_ITEM_IDS.NOTIFICATIONS,
  to: '/portal/approvals',
  label: 'Notifications',
  icon: Bell,
  trackedSectionId: PORTAL_NAV_SECTION_IDS.NOTIFICATIONS,
};

const MORE_DOCK_ITEM: PortalDockLinkItem = {
  kind: 'link',
  id: PORTAL_DOCK_ITEM_IDS.MORE,
  to: '/portal/more',
  label: 'More',
  icon: MoreHorizontal,
  trackedSectionId: PORTAL_NAV_SECTION_IDS.MORE,
};

export const PORTAL_MOBILE_DOCK_ITEMS: PortalDockItem[] = [
  HOME_DOCK_ITEM,
  EXPLORE_DOCK_ITEM,
  NOTIFICATIONS_DOCK_ITEM,
  MORE_DOCK_ITEM,
];

export const PORTAL_DESKTOP_DOCK_ITEMS: PortalDockItem[] = [
  HOME_DOCK_ITEM,
  EXPLORE_DOCK_ITEM,
  NOTIFICATIONS_DOCK_ITEM,
  MORE_DOCK_ITEM,
];

export interface PortalDeepRouteOwnership {
  path: string;
  navItemId: PortalNavItemId;
}

/**
 * Authenticated Portal routes that are valid router destinations but are not
 * entries in the top-level navigation directory. Keep this list in lockstep with
 * the protected `/portal` children in router.tsx.
 */
export const PORTAL_DEEP_ROUTE_OWNERSHIP: PortalDeepRouteOwnership[] = [
  { path: '/portal/dashboard', navItemId: PORTAL_NAV_ITEM_IDS.DASHBOARD },
  { path: '/portal/pathway', navItemId: PORTAL_NAV_ITEM_IDS.EXPLORE },
  { path: '/portal/courses/:courseSlug', navItemId: PORTAL_NAV_ITEM_IDS.CLASSES },
  { path: '/portal/tutoring/:slug', navItemId: PORTAL_NAV_ITEM_IDS.TEACHERS },
  { path: '/portal/self-paced/:slug', navItemId: PORTAL_NAV_ITEM_IDS.SELF_PACED },
  { path: '/portal/academy/products/:slug', navItemId: PORTAL_NAV_ITEM_IDS.ACADEMY },
  { path: '/portal/academy/checkout/:sku', navItemId: PORTAL_NAV_ITEM_IDS.ACADEMY },
  { path: '/portal/academy/orders/:intentId', navItemId: PORTAL_NAV_ITEM_IDS.ACADEMY },
  { path: '/portal/academy/reports/:sessionId', navItemId: PORTAL_NAV_ITEM_IDS.ACADEMY },
  { path: '/portal/checkout/class/:classId', navItemId: PORTAL_NAV_ITEM_IDS.CLASSES },
  { path: '/portal/challenge/:slug', navItemId: PORTAL_NAV_ITEM_IDS.CHALLENGE },
  { path: '/portal/challenge/:slug/register', navItemId: PORTAL_NAV_ITEM_IDS.CHALLENGE },
  { path: '/portal/challenge/:slug/report', navItemId: PORTAL_NAV_ITEM_IDS.CHALLENGE },
  { path: '/portal/learning/classes/:id', navItemId: PORTAL_NAV_ITEM_IDS.PARENT_COURSES },
  { path: '/portal/learning/self-paced/:id', navItemId: PORTAL_NAV_ITEM_IDS.PARENT_COURSES },
  { path: '/portal/family', navItemId: PORTAL_NAV_ITEM_IDS.DASHBOARD },
  { path: '/portal/family/new', navItemId: PORTAL_NAV_ITEM_IDS.DASHBOARD },
  { path: '/portal/family/:kidId', navItemId: PORTAL_NAV_ITEM_IDS.DASHBOARD },
  { path: '/portal/family/:kidId/study', navItemId: PORTAL_NAV_ITEM_IDS.DASHBOARD },
  { path: '/portal/study', navItemId: PORTAL_NAV_ITEM_IDS.DASHBOARD },
  { path: '/portal/family/:kidId/projects', navItemId: PORTAL_NAV_ITEM_IDS.DASHBOARD },
  { path: '/portal/family/:kidId/passport', navItemId: PORTAL_NAV_ITEM_IDS.DASHBOARD },
  { path: '/portal/family/:kidId/images', navItemId: PORTAL_NAV_ITEM_IDS.DASHBOARD },
  { path: '/portal/family/:kidId/settings', navItemId: PORTAL_NAV_ITEM_IDS.DASHBOARD },
  { path: '/portal/family/:kidId/companion', navItemId: PORTAL_NAV_ITEM_IDS.DASHBOARD },
  { path: '/portal/wallet/topup', navItemId: PORTAL_NAV_ITEM_IDS.WALLET },
  { path: '/portal/wallet/auto-topup', navItemId: PORTAL_NAV_ITEM_IDS.WALLET },
  { path: '/portal/wallet/stars-guide', navItemId: PORTAL_NAV_ITEM_IDS.WALLET },
  { path: '/portal/usage/:kidId', navItemId: PORTAL_NAV_ITEM_IDS.USAGE },
  { path: '/portal/desktop/memory', navItemId: PORTAL_NAV_ITEM_IDS.DESKTOP },
  { path: '/portal/desktop/pair', navItemId: PORTAL_NAV_ITEM_IDS.DESKTOP },
  { path: '/portal/audit', navItemId: PORTAL_NAV_ITEM_IDS.MORE },
  // Family Apps open from the Home section (photo-nutrition-p1-integration-prd.md §3.1).
  { path: '/portal/apps/:pluginId', navItemId: PORTAL_NAV_ITEM_IDS.DASHBOARD },
  { path: '/portal/audit/project/:id', navItemId: PORTAL_NAV_ITEM_IDS.APPROVALS },
];

function portalNavItemMatchesExactPath(item: PortalNavItem, pathname: string): boolean {
  return pathname === item.to || pathname === `${item.to}/`;
}

function getPortalRouteOwner(pathname: string): PortalNavItem | undefined {
  const deepRouteOwner = PORTAL_DEEP_ROUTE_OWNERSHIP.find((route) =>
    matchPath({ path: route.path, end: true }, pathname),
  );
  if (deepRouteOwner) return getPortalNavItem(deepRouteOwner.navItemId);

  return PORTAL_NAV_ITEMS.find((item) => portalNavItemMatchesExactPath(item, pathname));
}

/** Returns the one dock item that represents the current route at this viewport. */
export function getPortalDockSelectedItemId(pathname: string): string | null {
  const matchedItem = getPortalRouteOwner(pathname);

  if (!matchedItem) return null;
  if (isPortalWalletPath(pathname)) return null;
  const matchedSection = PORTAL_NAV_SECTIONS.find((section) =>
    section.items.some((item) => item.id === matchedItem.id),
  );
  if (matchedSection?.id === PORTAL_NAV_SECTION_IDS.ACCOUNT) {
    return matchedItem.id === PORTAL_NAV_ITEM_IDS.SETTINGS ||
      matchedItem.id === PORTAL_NAV_ITEM_IDS.BILLING
      ? PORTAL_DOCK_ITEM_IDS.MORE
      : null;
  }
  if (matchedSection?.id === PORTAL_NAV_SECTION_IDS.FAMILY) {
    return PORTAL_DOCK_ITEM_IDS.MORE;
  }
  const categoryItem = PORTAL_MOBILE_DOCK_ITEMS.find(
    (item) => item.trackedSectionId === matchedSection?.id,
  );
  return categoryItem?.id ?? null;
}

/** Keep dock badges compact without hiding ordinary two-digit notification totals. */
export function formatPortalNotificationBadgeCount(count: number): string {
  return count > 99 ? '99+' : String(count);
}
